import { computed } from 'vue'
import { useStore } from 'vuex'
import { EVENT_TYPES, globalEmit } from './eventBus'
import { getUserToken, isTokenExpired, removeUserToken, removeLastActiveTime } from './user'
import store from '@/store'

/**
 * 检查用户是否已登录
 * 如果未登录，会触发打开登录框的事件
 *
 * @param options 配置选项
 * @param options.loginType 登录类型，默认为 1（邮箱登录）
 * @param options.redirectType 登录成功后的重定向类型（可选），如 'chat', 'study', 'apexam' 等
 * @param options.pendingChatDemo 为 true 时表示因 welcome demo 触发的登录框，App 会保留「登录后继续 demo」的 session 标记
 * @returns 如果已登录返回 true，未登录返回 false
 *
 * @example
 * // 方式1: 在点击事件中直接使用
 * const handleClick = () => {
 *     if (!checkAuth({ redirectType: 'chat' })) {
 *         return; // 未登录，已自动打开登录框，直接返回
 *     }
 *     // 已登录，继续执行后续逻辑
 *     router.push({ name: 'chat' });
 * };
 *
 * @example
 * // 方式2: 在需要登录的功能前检查
 * const startStudy = () => {
 *     if (checkAuth({ redirectType: 'study' })) {
 *         // 已登录，执行学习功能
 *         router.push({ name: 'study' });
 *     }
 *     // 未登录时，checkAuth 会自动打开登录框
 * };
 *
 * @example
 * // 方式3: 使用 composable（推荐在组件中使用）
 * const { check, isLoggedIn } = useAuthCheck({ redirectType: 'chat' });
 *
 * const handleAction = () => {
 *     if (check()) {
 *         // 已登录，执行操作
 *     }
 * };
 */
export function checkAuth(options?: { loginType?: number; redirectType?: string; pendingChatDemo?: boolean }): boolean {
    const token = getUserToken()
    const isLoggedIn = store.getters['user/isLoggedIn']

    // 检查是否有 token 和登录状态
    if (token && isLoggedIn) {
        // Token 7 天滚动过期检查
        if (isTokenExpired()) {
            removeUserToken()
            removeLastActiveTime()
            store.dispatch('user/clearUserInfo')
        } else {
            return true
        }
    }

    // 未登录，触发打开登录框
    globalEmit(EVENT_TYPES.GO_LOGIN, {
        loginType: options?.loginType ?? 1,
        type: options?.redirectType ?? '',
        pendingChatDemo: options?.pendingChatDemo === true
    })

    return false
}

/**
 * Composable 函数，用于在组件中检查登录状态
 * 推荐在 Vue 组件中使用，可以获取响应式的登录状态
 *
 * @param options 配置选项
 * @param options.loginType 登录类型，默认为 1（邮箱登录）
 * @param options.redirectType 登录成功后的重定向类型（可选）
 * @returns 返回检查函数和登录状态
 *
 * @example
 * // 在组件中使用
 * <script setup lang="ts">
 * import { useAuthCheck } from '@/utils/authCheck';
 * import { useRouter } from 'vue-router';
 *
 * const router = useRouter();
 * const { check, isLoggedIn } = useAuthCheck({ redirectType: 'chat' });
 *
 * const handleNavigate = () => {
 *     if (check()) {
 *         router.push({ name: 'chat' });
 *     }
 * };
 * </script>
 *
 * <template>
 *     <button @click="handleNavigate" :disabled="!isLoggedIn">
 *         开始聊天
 *     </button>
 * </template>
 */
export function useAuthCheck(options?: { loginType?: number; redirectType?: string; pendingChatDemo?: boolean }) {
    const vuexStore = useStore()
    const isLoggedIn = computed(() => vuexStore.getters['user/isLoggedIn'])

    const check = () => {
        return checkAuth(options)
    }

    return {
        check,
        isLoggedIn
    }
}

export const checkAuthOnly = (): boolean => {
    const vuexStore = useStore()
    const isLoggedIn = computed(() => vuexStore.getters['user/isLoggedIn'])

    return isLoggedIn.value
}
